import { api } from '../../../shared/api';
import type { User } from '../../auth/types';
import type { RoleUpdateRequest, RoleUpdateResponse } from './roleApi';

export interface Organization {
  id: string;
  name: string;
  address: string;
  phoneNumber: string;
  logo?: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingStatus {
  user: User;
  organization: Organization | null;
  isOnboardingComplete: boolean;
}

// RoleUpdateRequest is now imported from roleApi to avoid conflicts

export interface OrganizationCreateRequest {
  name: string;
  address: string;
  phoneNumber: string;
}

export interface OnboardingStatusResponse {
  success: boolean;
  data: OnboardingStatus;
}

// RoleUpdateResponse is now imported from roleApi to avoid conflicts

export interface OrganizationCreateResponse {
  success: boolean;
  message: string;
  data: {
    organization: Organization;
  };
}

export const onboardingApi = {
  // Get onboarding status
  getOnboardingStatus: async (): Promise<OnboardingStatus> => {
    const res = await api.get<OnboardingStatusResponse>('/onboarding/status');
    return res.data;
  },

  // Update user role
  updateUserRole: async (role: RoleUpdateRequest): Promise<User> => {
    const res = await api.post<RoleUpdateResponse>('/onboarding/role', role);
    return res.data.user;
  },

  // Create organization
  createOrganization: async (
    organization: OrganizationCreateRequest,
  ): Promise<Organization> => {
    const res = await api.post<OrganizationCreateResponse>(
      '/onboarding/organization',
      organization,
    );
    return res.data.organization;
  },

  // Get user's organization
  getOrganization: async (): Promise<Organization | null> => {
    const res = await api.get<{
      success: boolean;
      data: { organization: Organization | null };
    }>('/onboarding/organization');
    return res.data.organization;
  },

  // Complete onboarding
  completeOnboarding: async (): Promise<void> => {
    await api.post('/onboarding/complete');
  },

  // Get upload URL for organization logo
  getUploadUrl: async (
    filename: string,
    contentType: string,
  ): Promise<{ signedUrl: string; token: string; path: string }> => {
    const response = await api.post<{
      success: boolean;
      data: {
        signedUrl: string;
        token: string;
        path: string;
      };
    }>('/organization/upload-url', { filename, contentType });
    return response.data;
  },
};
